import {Injectable} from "@angular/core";
import {Company} from "./_models/company";
import {Project} from "./_models/project";
import {ComponentStore} from "@ngrx/component-store";
import {CompaniesService} from "./_services/companies.service";
import {ProjectsService} from "./_services/projects.service";
import {SubRole, User} from "./_models/user";
import {switchMap, tap} from "rxjs/operators";
import {EMPTY, Observable, of} from "rxjs";
import {Area} from "./_models/area";
import {Board} from "./components/admin/components/colorboard/models";
import {Task} from "./_models/task";
import {FilterSettings} from "./_models/filterSettings";
import {AuthService} from "./_services/auth.service";
import {EntityTypeWithTabs} from "./_models/entityWithTabs";
import {getLinkedElementsIdsAndNames} from "./_util/utils";
import {Trade} from "./_models/trade";

export interface GlobalState {
  company: Company | null;
  projectId: number | null;
  tradePublicId: string | null;
  trade: Trade | null;
  boardPublicId: string | null;
  isMobile: boolean;
  isNotDesktop: boolean;
  isIOS: boolean;
  currentUser: User | undefined;
  currentUserSubRole: SubRole | undefined;
  filterSettings: FilterSettings | null;
}

@Injectable({ providedIn: 'root' })
export class GlobalStore extends ComponentStore<GlobalState> {
  constructor(
    private companyService: CompaniesService,
    private projectService: ProjectsService,
    private authService: AuthService,
  ) {
    super({
      company: null,
      projectId: null,
      tradePublicId: null,
      trade:  null,
      boardPublicId: null,
      isMobile: window.innerWidth <= 639,
      isNotDesktop: window.innerWidth <= 1279,
      isIOS:
        /iPad|iPhone|iPod/.test(navigator.userAgent) ||
        (navigator.userAgent.includes('Mac') && 'ontouchend' in document),
      currentUser: undefined,
      currentUserSubRole: SubRole.VIEWER,
      filterSettings: null,
    });

    this.authService.currentUser$.subscribe((user) => this.updateCurrentUser(user));
  }


  updateCompanyAndProjectOnAreaPage$(area: Area): Observable<void> {
    if (this.get().company?.id !== area.company.id) {
      return this.companyService.getCompanyByIdShort(area.company.id).pipe(
        tap((company: Company) => {
          this.patchState({ company, projectId: area.project.id, boardPublicId: null });
        }),
        switchMap(() => of(void 0))
      );
    } else if (this.get().projectId !== area.project.id) {
      this.setProjectId(area.project.id);
      return of(void 0);
    }
    return of(void 0);
  }

  updateCompanyAndProjectOnElementPage$(element: EntityTypeWithTabs): Observable<void> {
    let elementInfo = getLinkedElementsIdsAndNames(element);
    let {companyId, projectId} = elementInfo;
    if (this.get().company?.id !== companyId) {
      return this.companyService.getCompanyByIdShort(companyId).pipe(
        tap((company: Company) => {
          this.patchState({ company, projectId: projectId, boardPublicId: null });
        }),
        switchMap(() => of(void 0))
      );
    } else if (this.get().projectId !== projectId) {
      this.setProjectId(projectId);
      return of(void 0);
    }
    return of(void 0);
  }

  setCompany(company: Company) {
    this.patchState({ company });
  }

  readonly setTrade = this.updater((state, trade: Trade | null) => ({
    ...state,
    trade,
    tradePublicId: trade?.publicId ?? null
  }));

  readonly updateTrade = this.updater((state, trade: Trade) => ({
    ...state,
    trade,
    tradePublicId: trade?.publicId ?? null
  }));

  readonly resetTrade = this.updater(state => ({
    ...state,
    trade: null,
    tradePublicId: null
  }));

  readonly setFilterSettings = this.updater((state, filterSettings: FilterSettings) => ({
    ...state,
    filterSettings,
  }));

  // Updater to set projectId
  readonly setProjectId = this.updater((state, projectId: number | null) => ({
    ...state,
    projectId,
  }));

  // Updater to set boardId
  readonly setBoardId = this.updater((state, boardId: string | null) => ({
    ...state,
    boardPublicId: boardId,
  }));

  // Reset state when no companyId is present
  resetState() {
    this.patchState({
      company: null,
      projectId: null,
      boardPublicId: null,
      tradePublicId: null
    });
  }

  // Handle window resize events
  onResize() {
    const isMobile = window.innerWidth <= 639;
    const isNotDesktop = window.innerWidth <= 1279;

    if (this.get().isMobile !== isMobile || this.get().isNotDesktop !== isNotDesktop) {
      this.patchState({ isMobile, isNotDesktop });
    }
  }

  // Selectors
  readonly currentUser$ = this.select((state) => state.currentUser);
  readonly currentUserSubRole$ = this.select((state) => state.currentUserSubRole);
  readonly isMobile$ = this.select((state) => state.isMobile);
  readonly isNotDesktop$ = this.select((state) => state.isNotDesktop);
  readonly isIOS$ = this.select((state) => state.isIOS);
  readonly company$ = this.select((state) => state.company);
  readonly companyId$ = this.select((state) => state.company?.id ?? null);
  readonly companyName$ = this.select((state) => state.company?.name ?? null);
  readonly project$ = this.select((state) =>
    state.company?.projects.find((e) => e.id === state.projectId) ?? null
  );
  readonly projectId$ = this.select((state) => state.projectId);
  readonly projectName$ = this.select((state) =>
    state.company?.projects.find((e) => e.id === state.projectId)?.name ?? null
  );
  readonly boardPublicId$ = this.select((state) => state.boardPublicId);
  readonly board$ = this.select((state) =>
    state.company?.colorBoards.find((e) => e.publicId === state.boardPublicId) ?? null
  );
  readonly trade$ = this.select(state => state.trade);
  readonly tradePublicId$ = this.select(state => state.tradePublicId ?? null);
  readonly filterSettings$ = this.select((state) => state.filterSettings ?? null);

  // Updater to update current user
  readonly updateCurrentUser = this.updater((state, user: User | null) => {
    if (user) return { ...state, currentUser: { ...user }};
    else return { ...state, currentUser: null };
  });

  readonly updateCurrentUserSubRole = this.updater((state, subRole: SubRole | null) => {
    return { ...state, currentUserSubRole: subRole };
  });

  // Updater to add a project to the company
  readonly addProject = this.updater((state, project: Project) => {
    if (!state.company) return state;
    const updatedCompany = {
      ...state.company,
      projects: [project, ...state.company.projects],
    };
    return { ...state, company: updatedCompany };
  });

  readonly addTrade = this.updater((state, trade: Trade) => {
    if (!state.company) return state;
    const updatedCompany = {
      ...state.company,
      trades: [trade, ...state.company.trades],
    };
    return { ...state, company: updatedCompany };
  });

  // Updater to update a project
  readonly updateProject = this.updater((state, project: Project) => {
    if (!state.company) return state;

    const updatedProjects = state.company.projects.map((proj) =>
      proj.id !== project.id ? proj : { ...proj, ...project }
    );

    const updatedCompany = { ...state.company, projects: updatedProjects };
    return { ...state, company: updatedCompany };
  });

  // Effect to load project progress
  readonly loadProjectProgress = this.effect<void>((trigger$) =>
    trigger$.pipe(
      switchMap(() => {
        const { projectId, company } = this.get();
        if (!projectId || !company) return EMPTY;

        return this.projectService.getProjectProgressById(projectId).pipe(
          tap((projectProgress) => {
            const updatedProjects = company.projects.map((proj) =>
              proj.id !== projectId ? proj : { ...proj, status: projectProgress }
            );

            const updatedCompany = { ...company, projects: updatedProjects };
            this.patchState({ company: updatedCompany });
          })
        );
      })
    )
  );

  // Updater to delete projects from the company
  readonly deleteProjectsFromCompany = this.updater((state, projectIds: number[]) => {
    if (!state.company) return state;

    const updatedProjects = state.company.projects.filter(
      (p) => !projectIds.includes(p.id)
    );

    const updatedCompany = { ...state.company, projects: updatedProjects };
    return { ...state, company: updatedCompany };
  });

  //Updater to update company
  readonly updateCompany = this.updater((state, company: Company | null) => ({
    ...state,
    company,
  }));

  // Updater to add a Color Board to the company
  readonly addColorBoard = this.updater((state, board: Board) => {
    if (!state.company) return state;
    const updatedCompany = {
      ...state.company,
      colorBoards: [board, ...state.company.colorBoards],
    };
    return { ...state, company: updatedCompany };
  });

  // Updater to update a Color Board
  readonly updateColorBoard = this.updater((state, board: Board) => {
    if (!state.company) return state;

    const updatedBoards = state.company.colorBoards.map((bed) =>
      bed.id !== board.id ? bed : { ...bed, ...board }
    );

    const updatedCompany = { ...state.company, colorBoards: updatedBoards };
    return { ...state, company: updatedCompany };
  });

  // Updater to delete projects from the company
  readonly deleteColorBoardsFromCompany = this.updater((state, boardIds: number[]) => {
    if (!state.company) return state;

    const updatedBoards = state.company.colorBoards.filter(
      (b) => !boardIds.includes(b.id)
    );

    const updatedCompany = { ...state.company, colorBoards: updatedBoards };
    return { ...state, company: updatedCompany };
  });
}
